import { inject, Injectable, Signal } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, map, merge, Observable, shareReplay, startWith, Subject, tap, withLatestFrom } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { SortDirection } from '@shared/components/table-implementation/types/sorting-directions';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { TableConfigStoreService } from './table-config-store.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { TableConfig, TableOptions } from '@shared/components/table-implementation/types/table-config';
import { getColumnOfTableConfig, TableColumn } from '@shared/components/table-implementation/types/column/header.type';
import { TableColumnConfig } from '@shared/components/table-implementation/types/column/column-config.type';
import { TableFilterConfig } from '@shared/components/table-implementation/types/filter/table-filter-config.type';

/**
 * Provide together with the {@link TABLE_SCOPE | TABLE_SCOPE} injection token and the {@link TableConfigStoreService | TableConfigStoreService} as a {@link TABLE_DEFAULT_CONFIG | Default-Config}.
 * Prefer using the provideTableConfigService-function instead.
 */
@Injectable()
export class TableConfigService {
  private readonly _tableConfigStoreService: TableConfigStoreService = inject(TableConfigStoreService);

  private _selectedConfigModified$: Subject<void> = new Subject<void>();

  private _selectedConfig$: BehaviorSubject<TableConfig | undefined> = new BehaviorSubject<TableConfig | undefined>(undefined);

  private _preparedSelectedConfig$: Observable<TableConfig | undefined> = merge(
    this._selectedConfig$,
    this._tableConfigStoreService.configs$.pipe(
      withLatestFrom(this._selectedConfig$),
      map(([ configs, selected ]: [TableConfig[], TableConfig | undefined]) => configs.find((config: TableConfig) => config.id === selected?.id))
    )
  ).pipe(
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public selectedConfig$: Observable<TableConfig> = combineLatest([
    this._preparedSelectedConfig$,
    this._tableConfigStoreService.defaultConfig$,
    this._tableConfigStoreService.favoriteConfigs$
  ]).pipe(
    map(([ selectedConfig, defaultConfig, favorites ]: [TableConfig | undefined, TableConfig, TableConfig[]]) =>
      selectedConfig ?? favorites[ 0 ] ?? defaultConfig)
  );

  private _selectedConfig: Signal<TableConfig | undefined> = toSignal(this.selectedConfig$);

  public isSelectedConfigDirty$: Observable<boolean> = merge(
    this._selectedConfigModified$.pipe(map(() => true)),
    this.selectedConfig$.pipe(map(() => false))
  ).pipe(
    startWith(false),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  public select (config: TableConfig): void {
    this._selectedConfig$.next(config);
  }

  public rename (config: TableConfig, newName: string): Observable<HttpResponse<void>> {
    const patch: TableConfig = { ...config, name: newName };

    return this._tableConfigStoreService.update(config.id, patch).pipe(
      tap(() => this.patchLocally(patch))
    );
  }

  public markFavorite (config: TableConfig, isFavorite: boolean): Observable<HttpResponse<void>> {
    const patch: TableConfig = { ...config, isFavorite };

    return this._tableConfigStoreService.update(config.id, patch).pipe(
      tap(() => this.patchLocally(patch))
    );
  }


  patchTableOptionsLocally (options: TableOptions): void {
    const selected: TableConfig | undefined = this._selectedConfig();

    if (selected)
      this.patchLocally({ ...selected, options });
  }

  public patchColumnConfigLocally (config: TableColumnConfig): void {
    const selected: TableConfig | undefined = this._selectedConfig();

    if (selected)
      this.patchLocally({ ...selected, columnConfig: config });
  }

  public patchFilterConfigLocally (config: TableFilterConfig): void {
    const selected: TableConfig | undefined = this._selectedConfig();

    if (selected)
      this.patchLocally({ ...selected, filterConfig: config });
  }

  private patchLocally (config: TableConfig): void {
    const selected: TableConfig | undefined = this._selectedConfig();

    if (selected)
      this._selectedConfig$.next(config);

    if (!config.isDefault)
      this._selectedConfigModified$.next();
  }

  public changeColumnSortDirection (field: string, newSortDirection: SortDirection): void {
    if (!this._selectedConfig()) return;

    // eslint-disable-next-line @tseslint/no-non-null-assertion
    const selectedConfig: TableConfig = this._selectedConfig()!;
    const header: TableColumn | undefined = getColumnOfTableConfig(selectedConfig.columnConfig, field);

    if (header && header.options.sortDirection !== newSortDirection) {
      header.options.sortDirection = newSortDirection;

      const fieldsSortedByOptions: string[] = selectedConfig.columnConfig.fieldsSortedByOptions;
      const itemIndex: number = fieldsSortedByOptions.indexOf(header.field);

      if (itemIndex === -1 || fieldsSortedByOptions.length === 0)
        fieldsSortedByOptions.push(header.field);
      else
        moveItemInArray(fieldsSortedByOptions, itemIndex, fieldsSortedByOptions.length - 1);

      selectedConfig.columnConfig.fieldsSortedByOptions = fieldsSortedByOptions;
      this.patchLocally(structuredClone(selectedConfig));
    }
  }

  public changeColumnVisibility (field: string, isVisible: boolean): void {
    if (!this._selectedConfig()) return;

    // eslint-disable-next-line @tseslint/no-non-null-assertion
    const selectedConfig: TableConfig = this._selectedConfig()!;
    const header: TableColumn | undefined = getColumnOfTableConfig(selectedConfig.columnConfig, field);

    if (!header) return;

    header.options.isVisible = isVisible;
    this.patchLocally(structuredClone(selectedConfig));
  }

  public changeColumnFixed (field: string, isFixed: boolean): void {
    const clonedTableConfig: TableConfig | undefined = structuredClone(this._selectedConfig());

    if (!clonedTableConfig) return;

    const matchingHeader: TableColumn | undefined = getColumnOfTableConfig(clonedTableConfig.columnConfig, field);

    if (matchingHeader && matchingHeader.options.isFixed !== isFixed) {
      const allColumns: TableColumn[] = clonedTableConfig.columnConfig.columns;
      const previousIndex: number = clonedTableConfig.columnConfig.columns.findIndex((columnToCheck: TableColumn) => columnToCheck.field === field);
      const newIndex: number = clonedTableConfig.columnConfig.columns.findIndex((columnToCheck: TableColumn) => !columnToCheck.options.isFixed);

      if (newIndex !== -1 && previousIndex !== -1) {
        moveItemInArray(allColumns, previousIndex, newIndex);
        clonedTableConfig.columnConfig.columns = allColumns;
      }

      matchingHeader.options.isFixed = isFixed;
      this.patchLocally(clonedTableConfig);
    }
  }
}
